import React,{ useState,useEffect,useRef} from "react";
import moment from "moment";
import axios from '../../../plugins/axios';
import Paging from "./Common/Paging.jsx"
import { useRecoilState } from "recoil";
import { accountInfo } from "../../../plugins/adminRecoil.js";
import { useTranslation } from 'react-i18next';
import Toast from "../../../plugins/useToast";

import {ReactComponent as IconSearch } from "../../../assets/icons/ic-search.svg";
import {ReactComponent as IconArrowUp} from '../../../assets/icons/ic-arrow-up.svg'
import {ReactComponent as IconArrowDown} from '../../../assets/icons/ic-arrow-down.svg'
import {ReactComponent as IconCheck} from '../../../assets/icons/ic-check_blue.svg';

const Account = ({handSelectMenu, handleSetConfirm}) => {
    const { t } = useTranslation()
    const isFirstRender = useRef(true);
    const [globalAccount, setGlobalAccount] = useRecoilState(accountInfo) //Account 정보 상태 관리
    const countOptions = [50, 100, t('admin.all')]; //Item 고정
    const [searchValue, setSearchValue] = useState(""); //검색
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [itemCount, setItemCount] = useState(50);         
    const [searchSort, setSearchSort] = useState({
        column: '',
        param : '',
        order: 'ASC'
    });
    const [pagingInfo, setPagingInfo] = useState({
        currentPage: 1,
        totalPages: 50,        
        pageSize: 5,
        totalCount : 0
    });    
    const [accountData, setAccountData] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false); //전체 아이템 선택 관리 상태.

    // ✅ 정렬 시 Icon 변경
    const getSortIcon = (column) => {
        if (searchSort.column === column) {
            return searchSort.order === 'ASC' ? 
                <IconArrowUp className="inline-block" /> : 
                <IconArrowDown className="inline-block" />;
        }
        return <IconArrowDown className="inline-block" />; // 기본 아이콘
    };

    // ✅ Sort UseState 변경
    const handleSort = (column,param) => {
        setSearchSort((prev) => ({
            column: column,
            param : param,
            order: prev.column === column && prev.order === 'ASC' ? 'DESC' : 'ASC'
        }));
    };    

    // ✅ 페이지 이동 함수
    const goToPage = (page) => {
        if (page < 1 || page > pagingInfo.totalPages) return;
        setPagingInfo((prev) => ({ ...prev, currentPage: page }));
        getUserList(page-1);
    };

    // ✅ User List 가져오기
    const getUserList = async (page = 0) => {
        try{            
            const keyword = searchValue;            
            const size = itemCount == t('admin.all') ? 0 : itemCount;            
            const sortBy = searchSort.param;
            const sort = searchSort.order;
            const url = `/users?page=${page}&${keyword ? 'keyword=' + keyword + '&' : ''}${size ? 'size=' + size + '&' : ''}${sortBy ? 'sortBy=' + sortBy + '&' : ''}${sort ? 'sort=' + sort : ''}`;

            const resUser = await axios.get(url);
            if(resUser.code != 200) throw resUser;            

            //데이터 시 isCheck를 넣어주고, CheckBox로 쓰기 위함.
            const accountListWithCheck = resUser.data.content.map(account => ({
                ...account,
                isCheck: false
            }));

            setAccountData(accountListWithCheck);
            setIsAllSelected(false); // 전체 선택 해제

            //Paging
            setPagingInfo({
                currentPage: (resUser.data.page+1),
                totalPages: resUser.data.totalPages,
                pageSize: resUser.data.size,
                totalCount : resUser.data.totalElements
            });
        }
        catch(err){
            console.warn(err);
        }
    }

    // ✅ Enter키 입력 시 검색 
    const handleEnter = (e) => {
        if (e.keyCode === 13) {            
            getUserList(0);
        }
    }

    // ✅ Delete Account 버튼 이벤트
    const handleDeleteAccount = async () => {
        try{
            const selectedAccounts = accountData.filter(account => account.isCheck);
            if (selectedAccounts.length === 0) {
                Toast({ isSuccess: false, msg: t('admin.Account.confirm.noAccountSelectedForDelete') });
                return;
            }
            const userIds = selectedAccounts.map(account => account.id);            
            const res = await axios.delete('/users', {params : {userIds : userIds.join(',')}}); //백엔드에서 배열로 받도록 변경            
            if(res.code != 200) throw res;

            Toast({ isSuccess: true, msg: t('admin.Account.confirm.deleteSuccess') });
            getUserList(0);
        }
        catch(err){
            console.warn(err);
        }
    }

    // ✅ Reset Password Confirm 창 열기
    const handleResetComfirm = () => {
        handleSetConfirm('account','resetPwd2',true,handleResetPassword);
    }

    // ✅ Reset Password 변경 이벤트
    const handleResetPassword = async (password) => {
        try {
            const selectedAccounts = accountData.filter(account => account.isCheck);
            if (selectedAccounts.length === 0) {
                Toast({ isSuccess: false, msg: t('admin.Account.confirm.noAccountSelectedForChange') });
                return;
            }

            const userIds = selectedAccounts.map(account => account.id);
            const payload = {
                userIds: userIds,
                newPassword : password
            };
            const res = await axios.put('/users/reset-password', payload);
            if(res.code != 200) throw res;

            Toast({ isSuccess: true, msg: t('admin.Account.confirm.changeSuccess') });
            getUserList(0);
        }
        catch (err) {
            console.warn(err);
        }
    }

    // ✅ 검색, itemCount, Paging, 정렬 시 List 변경.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
    
        getUserList(0);
    }, [itemCount,searchSort]);

    useEffect(() => {
        getUserList();
        const handleClickOutside = (event) => {
            if (!event.target.closest('.select-dropdown')) {
                setIsDropdownOpen(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isAllSelected) {
            // 전체 선택: 모든 아이템의 isCheck를 true로 변경
            const updatedData = accountData.map(account => ({
                ...account,
                isCheck: true
            }));
            setAccountData(updatedData);
        } else {
            // 전체 해제: 모든 아이템의 isCheck를 false로 변경
            const updatedData = accountData.map(account => ({
                ...account,
                isCheck: false
            }));
            setAccountData(updatedData);
        }
    }, [isAllSelected]);

    const isAnyChecked = accountData.some(account => account.isCheck);

    return (        
        <div>
            <h1 className="admin-title mb-[24px]">{t('admin.Account.title')}<span className="text-[#E1E4E5] font-pretendard text-[16px] font-normal leading-[160%] pl-[8px]">{`(${itemCount == t('admin.all') ? pagingInfo.totalCount : (pagingInfo.totalCount < (itemCount * pagingInfo.currentPage) ? pagingInfo.totalCount : (itemCount * pagingInfo.currentPage))}/${pagingInfo.totalCount})`}</span></h1>
            <div className="flex justify-between mb-[17px]">
                <div className="flex gap-[8px]">
                    <div className={`${!isAnyChecked ? 'disabled !cursor-default' : ''} admin-normal-btn w-[88px] h-[32px]`} onClick={isAnyChecked ? () => handleSetConfirm('account','delete', true,handleDeleteAccount,null,'','',t(`admin.Account.confirm.delete`)) : null  }>
                        <span className="btn-content">{t('admin.delete')}</span>
                    </div>
                    <div className={`${!isAnyChecked ? 'disabled !cursor-default' : ''} admin-normal-btn w-[148px] h-[32px]`} onClick={isAnyChecked ? () => handleSetConfirm('account','resetPwd', true,handleResetComfirm) : null}>
                        <span className="btn-content">{t('admin.Account.resetPassword')}</span>
                    </div>
                </div>
                <div className="flex items-center gap-[12px]"> 
                    <div className="relative">                        
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={handleEnter}
                            placeholder={t('admin.searchPlaceholder')}
                            className="admin-input w-[280px] pr-[30px]"
                            autoComplete="one-time-code"
                            maxLength={50}
                        />
                        <span className="ml-2 absolute bottom-[6px] right-2" onClick={() => getUserList(0)}>
                            <IconSearch className="cursor-pointer"/>
                        </span>
                    </div>                    
                    <div className="w-[1px] h-[24px] bg-[#0D0D0F]"></div>                                        
                    <div className="relative select-dropdown">
                        <div className="flex items-center justify-between w-[120px] h-[32px] px-[8px] rounded-[6px] border border-[#0D0D0F] text-[#DEDEDE] cursor-pointer bg-gradient-to-b from-[#5B5C60] to-[#303237] shadow-[inset_0_-2px_1px_0_rgba(255,255,255,0.10),inset_0_2px_0_0_rgba(255,255,255,0.12)]"
                        onClick={(e) => { e.stopPropagation(); setIsDropdownOpen(!isDropdownOpen);}}>
                            <div className="w-[200px]">{itemCount} {t('admin.items')}</div>
                            <div className="pointer-events-none">
                                {isDropdownOpen ? <IconArrowUp /> : <IconArrowDown />}
                            </div>
                        </div>
                        {isDropdownOpen && (
                            <div className="absolute top-[35px] bg-[#1F1F1F] w-[120px] py-[8px] rounded-[6px] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)] z-20">
                                {
                                    countOptions.map((option, index) => {
                                        return (
                                            <div key={index} className={`bg-[#1F1F1F] cursor-pointer h-[32px] flex items-center justify-between px-[8px] hover:bg-[#2F2F2F] 
                                                ${itemCount === option ? "text-[#3182F7]" : "text-white"}`}
                                                onClick={(e) => { e.stopPropagation(); setItemCount(option); setIsDropdownOpen(false); }}>
                                                {option}
                                                {itemCount === option && (
                                                    <IconCheck className="w-4 h-4 ml-2" />
                                                )}
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        )}
                    </div>
                    <div className="w-[1px] h-[24px] bg-[#0D0D0F]"></div>                    
                    <div className="admin-normal-btn add w-[120px] h-[32px]" onClick={() => handSelectMenu('AccountAdd')}>
                        <span className="btn-content">{t('admin.add')}</span>
                    </div>
                </div>
            </div>        
            <div className="mb-[26px]">
                <div className="scrollbar scrollbar-10px" style={{ maxHeight: '680px', borderBottom: '2px solid #0D0D0F' }}>
                    <table className="admin-table w-full">
                        <thead>
                            <tr className="h-[42px]">
                                <th className="w-[80px]">
                                    <div className="w-full flex justify-center items-center">
                                        <div className="rounded-[6px] p-[6px] w-[36px]">
                                            <label className="flex gap-[4px]">
                                                <input
                                                    type="checkbox"
                                                    value={isAllSelected}
                                                    checked={isAllSelected}
                                                    onChange={() => setIsAllSelected(!isAllSelected)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </th>                            
                                <th onClick={() => handleSort('Name','NAME')}>{t('admin.Account.form.Name')} {getSortIcon('Name')}</th>
                                <th onClick={() => handleSort('ID (E-mail)','LOGIN_ID')}>{t('admin.Account.form.ID')} {getSortIcon('ID (E-mail)')}</th>
                                <th onClick={() => handleSort('Phone number','PHONE_NUMBER')}>{t('admin.Account.form.PhoneNumber')} {getSortIcon('Phone number')}</th>
                                <th onClick={() => handleSort('Type','USER_ROLE')}>{t('admin.Account.form.Type')} {getSortIcon('Type')}</th>
                                <th onClick={() => handleSort('Department','DEPARTMENT')}>{t('admin.Account.form.Department')} {getSortIcon('Department')}</th>
                                <th onClick={() => handleSort('Status','USER_STATUS')}>{t('admin.Account.form.Status')} {getSortIcon('Status')}</th>
                                <th onClick={() => handleSort('Date Created','CREATED_AT')}>{t('admin.Account.form.DateCreated')} {getSortIcon('Date Created')}</th>
                                <th onClick={() => handleSort('Last Login','LAST_LOGIN')}>{t('admin.Account.form.LastLogin')} {getSortIcon('Last Login')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accountData && 
                                accountData.map((account, index) => {
                                    return (
                                        <tr className={`${account.isCheck ? 'selected-row' : `${index % 2 === 0 ? 'bg-[#202024]' : 'bg-[#29292E]'}`} h-[42px] cursor-pointer`} onClick={() => {setGlobalAccount(account); handSelectMenu('AccountEdit');}} key={`${account.id}-${index}`}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="w-full flex justify-center items-center">
                                                    <div className="rounded-[6px] p-[6px] w-[36px]">
                                                        <label className="flex gap-[4px]">
                                                            <input
                                                                type="checkbox"
                                                                value={account.isCheck}
                                                                checked={account.isCheck}
                                                                onChange={() => {
                                                                    const updatedData = accountData.map((item) =>
                                                                        item.id === account.id ? { ...item, isCheck: !item.isCheck } : item
                                                                    );
                                                                    setAccountData(updatedData);
                                                                }}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>        
                                            </td>                                        
                                            <td>{account.name || '-'}</td>
                                            <td>{account.loginId || '-'}</td>
                                            <td>{account.phoneNumber || '-'}</td>
                                            <td>{account.role || '-'}</td>
                                            <td>{account.departmentName || '-'}</td>
                                            <td>{account.status || '-'}</td>                                        
                                            <td>{account.createdAt && moment(account.createdAt).isValid() ? moment(account.createdAt).format("YYYY-MM-DD HH:mm:ss") : '-'}</td>
                                            <td>{account.lastLogin && moment(account.lastLogin).isValid() ? moment(account.lastLogin).format("YYYY-MM-DD HH:mm:ss") : '-'}</td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            <Paging currentPage={pagingInfo.currentPage} goToPage={goToPage} totalPages={pagingInfo.totalPages} visibleCount={5} />
        </div>
    )
};

export default Account;